import { Brain, Check, MessageSquare, Target, XIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import type {
  IncidentResolution,
  IncidentResolutionStep,
  IncidentResolutionStepType,
} from '@/lib/queries/rca/incidentResolutions';

import { formatTime, getRCAStatusBadge } from '../../-components/RCAUtils';

interface InvestigationTimelineProps {
  incidentResolution: IncidentResolution;
}

interface TimelineEventProps {
  step: IncidentResolutionStep;
  isLast: boolean;
}

const getEventIcon = (type: IncidentResolutionStepType): JSX.Element | null => {
  switch (type) {
    case 'acknowledged':
      return <Check className="w-5 h-5 text-green-500" />;
    case 'user_message':
      return <MessageSquare className="w-5 h-5 text-blue-500" />;
    case 'ai_answer':
      return <Brain className="w-5 h-5 text-purple-500" />;
    case 'ai_answer_failure':
      return <XIcon className="w-5 h-5 text-red-500" />;
  }

  return null;
};

const getStepTitle = (type: IncidentResolutionStep['type']): string => {
  switch (type) {
    case 'acknowledged':
      return 'Acknowledged';
    case 'user_message':
      return 'User Message';
    case 'ai_answer':
      return 'AI Answer';
    case 'ai_answer_failure':
      return 'AI Failed to Answer';
    default:
      return 'Unknown Step';
  }
};

const TimelineEventComponent = ({
  step,
  isLast,
}: TimelineEventProps): JSX.Element => {
  return (
    <div className="flex gap-4 mb-6">
      <div className="flex flex-col items-center">
        <div className="flex-shrink-0">{getEventIcon(step.type)}</div>
        {!isLast && (
          <div className="w-px h-full bg-gray-200 dark:bg-gray-700 mt-3" />
        )}
      </div>

      <div className="flex-1 max-w-3xl min-w-0">
        <div className="flex items-start gap-3 mb-2">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-base flex-1">
            {getStepTitle(step.type)}
          </h4>
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {formatTime(step.createdAt)}
          </span>
        </div>

        {step.message !== undefined && (
          <div
            className={`text-sm leading-relaxed mb-3 text-gray-600 dark:text-gray-400`}
          >
            <div className={'whitespace-pre-wrap'}>{step.message}</div>
          </div>
        )}

        {/* {!isChatMessage &&
          typeof event.findings === 'string' &&
          event.findings.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg mb-2">
              <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                <span className="font-medium">Findings: </span>
                {event.findings}
              </p>
            </div>
          )}

        {!isChatMessage &&
          typeof event.duration === 'number' &&
          event.duration > 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Duration: {formatDuration(event.duration)}
            </div>
          )} */}
      </div>
    </div>
  );
};

export const InvestigationTimeline = ({
  incidentResolution,
}: InvestigationTimelineProps): JSX.Element => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-shrink-0 w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
          <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Investigation Timeline
          </h3>
          <div className="flex items-center gap-3 mt-2">
            {getRCAStatusBadge(incidentResolution.status)}
            {incidentResolution.status === 'completed' &&
              typeof incidentResolution.confidence === 'number' &&
              incidentResolution.confidence > 0 && (
                <div className="flex items-center gap-1">
                  <Target className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {incidentResolution.confidence}% confidence
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Root Cause Summary */}
      {incidentResolution.status === 'completed' &&
        typeof incidentResolution.rootCause === 'string' &&
        incidentResolution.rootCause.length > 0 && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <h4 className="font-semibold text-green-900 dark:text-green-100 text-base mb-2">
              <FormattedMessage id="rca.summary.title" />
            </h4>
            <p className="text-green-800 dark:text-green-200 text-sm leading-relaxed">
              {incidentResolution.rootCause}
            </p>
          </div>
        )}

      {/* Timeline */}
      <div className="flex-1">
        <div className="max-h-[60vh] overflow-y-auto pr-2">
          {incidentResolution.steps.map((step, index) => (
            <TimelineEventComponent
              key={step.id}
              step={step}
              isLast={index === incidentResolution.steps.length - 1}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
